import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  hubService,
  type PromptTemplate,
  type CreatePromptTemplateDto,
} from '../../../services/hub.service';
import { AiConfigTabShell, AiConfigTable } from '../index';

const SYSTEM_BOT_ID = '__system__';
const DEFAULT_SYSTEM = 'chatbot';

export function PromptTemplatesTab() {
  const { getToken } = useAuth();
  const [items, setItems] = useState<PromptTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PromptTemplate | null>(null);
  const [form, setForm] = useState<CreatePromptTemplateDto>({
    system: DEFAULT_SYSTEM,
    botId: SYSTEM_BOT_ID,
    name: '',
    template: '',
    variables: undefined,
  });

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const data = await hubService.listPromptTemplates(SYSTEM_BOT_ID, undefined, token);
      setItems(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    if (!form.name.trim() || !form.template.trim()) return;
    try {
      const token = getToken();
      await hubService.createPromptTemplate(
        {
          ...form,
          botId: SYSTEM_BOT_ID,
          system: form.system || DEFAULT_SYSTEM,
        },
        token,
      );
      setCreating(false);
      setForm({ system: DEFAULT_SYSTEM, botId: SYSTEM_BOT_ID, name: '', template: '', variables: undefined });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    }
  }

  async function handleUpdate(id: string) {
    if (!editing) return;
    try {
      const token = getToken();
      await hubService.updatePromptTemplate(
        id,
        { template: editing.template, variables: editing.variables ?? undefined },
        token,
      );
      setEditing(null);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this prompt template?')) return;
    try {
      const token = getToken();
      await hubService.deletePromptTemplate(id, token);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  return (
    <AiConfigTabShell loading={loading} error={error}>
      <div className="space-y-6">
        {!creating ? (
          <button
            onClick={() => setCreating(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
          >
            Add Prompt Template
          </button>
        ) : (
          <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-3">
            <input
              placeholder="System (e.g. chatbot)"
              value={form.system}
              onChange={(e) => setForm((f) => ({ ...f, system: e.target.value }))}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
            <input
              placeholder="Name (e.g. intent_verification)"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
            <textarea
              placeholder="Template (use {{variable}} for placeholders)"
              value={form.template}
              onChange={(e) => setForm((f) => ({ ...f, template: e.target.value }))}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm font-mono"
              rows={8}
            />
            <div className="flex gap-2">
              <button onClick={handleCreate} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded">
                Save
              </button>
              <button onClick={() => setCreating(false)} className="px-3 py-1.5 text-sm bg-gray-200 rounded">
                Cancel
              </button>
            </div>
          </div>
        )}

        <AiConfigTable>
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">System</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Bot ID</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {items.map((item) => (
              <tr key={item.id}>
                <td className="px-4 py-2 text-sm font-mono">{item.system}</td>
                <td className="px-4 py-2 text-sm font-mono">{item.name}</td>
                <td className="px-4 py-2 text-sm font-mono">{item.botId}</td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => setEditing(item)}
                    className="text-blue-600 hover:underline text-sm mr-2"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="text-red-600 hover:underline text-sm"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </AiConfigTable>

        {editing && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-2xl w-full space-y-3 max-h-[90vh] overflow-y-auto">
              <h3 className="font-semibold">Edit {editing.name}</h3>
              <p className="text-xs text-gray-500">
                System: {editing.system} · Bot: {editing.botId}
              </p>
              <textarea
                placeholder="Template"
                value={editing.template}
                onChange={(e) =>
                  setEditing((x) => (x ? { ...x, template: e.target.value } : null))
                }
                className="w-full rounded border px-3 py-2 text-sm font-mono"
                rows={12}
              />
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => handleUpdate(editing.id)}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditing(null)}
                  className="px-3 py-1.5 text-sm bg-gray-200 rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AiConfigTabShell>
  );
}
